import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Trophy,
  Users,
  Wallet,
  Crown,
  TrendingUp,
  Clock,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Money } from "@/components/common/Money";
import { InlineLoading } from "@/components/common/Loading";
import { supabase } from "@/lib/supabase";
import { format, subDays } from "date-fns";

interface TodayBlock {
  depositsCount: number;
  depositsAmount: number;
  depositsFees: number;
  withdrawalsCount: number;
  withdrawalsAmount: number;
  withdrawalsPending: number;
  withdrawalsPendingAmount: number;
}

interface DashboardStats {
  totalDeposits: number;
  totalWithdrawals: number;
  netBalance: number;
  couponRisk: number;
  activeSubscriptions: number;
  totalUsers: number;
  weeklyRevenue: number;
  totalFeesAllTime: number;
  today: TodayBlock;
  daily: Array<{ date: string; deposits: number; withdrawals: number; coupon_wins: number }>;
}

function useAdminStats() {
  return useQuery({
    queryKey: ["admin-stats"],
    refetchInterval: 60_000,
    queryFn: async (): Promise<DashboardStats> => {
      const { data, error } = await supabase.functions.invoke<DashboardStats>("admin-stats");
      if (error) throw error;
      if (!data) throw new Error("empty");
      return data;
    },
  });
}

export function AdminDashboard() {
  const { t } = useTranslation();
  const { data, isLoading } = useAdminStats();

  if (isLoading) return <InlineLoading />;
  if (!data) return <p>—</p>;

  const since = format(subDays(new Date(), 7), "d MMM");
  const today = data.today;

  return (
    <div className="space-y-6">
      {/* === BLOC AUJOURD'HUI === */}
      <section className="rounded-xl border border-primary/20 bg-primary/[0.03] p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="size-4 text-primary" />
          <h2 className="text-sm font-semibold tracking-tight uppercase">Aujourd'hui</h2>
          <span className="text-2xs text-muted-foreground ml-auto">UTC</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <TodayKpi
            label="Dépôts réussis"
            icon={ArrowDownLeft}
            value={<Money amount={today.depositsAmount} compact />}
            hint={`${today.depositsCount} transaction${today.depositsCount > 1 ? "s" : ""}`}
            tone="success"
          />
          <TodayKpi
            label="Frais collectés"
            icon={TrendingUp}
            value={<Money amount={today.depositsFees} compact />}
            hint="revenus PayDunya"
            tone="success"
          />
          <TodayKpi
            label="Retraits demandés"
            icon={ArrowUpRight}
            value={<Money amount={today.withdrawalsAmount} compact />}
            hint={`${today.withdrawalsCount} transaction${today.withdrawalsCount > 1 ? "s" : ""}`}
            tone={today.withdrawalsAmount > today.depositsAmount ? "destructive" : "default"}
          />
          <TodayKpi
            label="En attente validation"
            icon={Clock}
            value={<Money amount={today.withdrawalsPendingAmount} compact />}
            hint={`${today.withdrawalsPending} à traiter`}
            tone={today.withdrawalsPending > 0 ? "warning" : "default"}
          />
        </div>
      </section>

      {/* === KPIs GLOBAUX === */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label={t("admin.stats.totalDeposits")} icon={ArrowDownLeft} value={<Money amount={data.totalDeposits} compact />} tone="success" />
        <Kpi label={t("admin.stats.totalWithdrawals")} icon={ArrowUpRight} value={<Money amount={data.totalWithdrawals} compact />} tone="destructive" />
        <Kpi label={t("admin.stats.netBalance")} icon={Wallet} value={<Money amount={data.netBalance} compact />} tone={data.netBalance >= 0 ? "success" : "destructive"} />
        <Kpi label="Revenus frais" icon={TrendingUp} value={<Money amount={data.totalFeesAllTime} compact />} tone="success" />
        <Kpi label={t("admin.stats.couponRisk")} icon={Trophy} value={<Money amount={data.couponRisk} compact />} tone="warning" />
        <Kpi label={t("admin.stats.activeSubscriptions")} icon={Crown} value={data.activeSubscriptions} />
        <Kpi label={t("admin.stats.totalUsers")} icon={Users} value={data.totalUsers} />
        <Kpi label={t("admin.stats.weeklyRevenue")} icon={Wallet} value={<Money amount={data.weeklyRevenue} compact />} tone="success" />
      </div>

      <Card>
        <CardContent className="py-4">
          <h3 className="text-sm font-semibold mb-3">Flux quotidiens — depuis {since}</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="deposits" name="Dépôts" stroke="hsl(var(--success))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="withdrawals" name="Retraits" stroke="hsl(var(--destructive))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="coupon_wins" name="Gains coupons" stroke="hsl(var(--warning))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4">
          <h3 className="text-sm font-semibold mb-3">Volumes 7 derniers jours</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="deposits" name="Dépôts" fill="hsl(var(--success))" radius={[6, 6, 0, 0]} />
                <Bar dataKey="withdrawals" name="Retraits" fill="hsl(var(--destructive))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

type Tone = "success" | "destructive" | "warning" | "default";

function toneClass(tone?: Tone) {
  switch (tone) {
    case "success": return "text-success";
    case "destructive": return "text-destructive";
    case "warning": return "text-warning";
    default: return "text-muted-foreground";
  }
}

function Kpi({ label, icon: Icon, value, tone }: { label: string; icon: React.ElementType; value: React.ReactNode; tone?: Tone }) {
  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center gap-2">
          <Icon className={`size-4 ${toneClass(tone)}`} />
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        </div>
        <p className="font-display text-2xl font-bold mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}

function TodayKpi({
  label,
  icon: Icon,
  value,
  hint,
  tone,
}: {
  label: string;
  icon: React.ElementType;
  value: React.ReactNode;
  hint?: string;
  tone?: Tone;
}) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-card p-3">
      <div className="flex items-center gap-1.5">
        <Icon className={`size-3.5 ${toneClass(tone)}`} />
        <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
      </div>
      <p className="font-display text-lg font-bold mt-1 num">{value}</p>
      {hint && <p className="text-2xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}
